export type RenameKey<T, OldKey extends keyof T, NewKey extends string> = {
	[K in keyof T as K extends OldKey ? NewKey : K]: T[K];
};

export type DiffKeys<T, U> = {
	[K in keyof T as K extends keyof U ? never : K]: T[K];
};

export type SetOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type AtLeastOne<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> & U[keyof U];

export type AnyObject = Record<string, unknown>;

export type ArrayElement<ArrayType extends readonly unknown[]> =
	ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

export type EmptyObject = Record<string, never>;

export type AnyFunction = (...args: any[]) => unknown;

export type Nullable<T> = T | null;

export type SetNonNullable<T> = {
	[Key in keyof T]: NonNullable<T[Key]>;
};

export type OneOrMore<T> = readonly [T, ...ReadonlyArray<T>];

// type guard for non-nullable values:
function assertIsDefinedGuard<T>(val: T): asserts val is NonNullable<T> {
	if (val === undefined || val === null) {
		throw new Error(`Expected value to be defined, but received ${val}`);
	}
}

export function assertDefined<T>(val: T) {
	assertIsDefinedGuard(val);
	return val;
}

export const assertUnreachable = (x: never): never => {
	throw new Error(`${x} was unhandled`);
};

export const isEvery = <T extends AnyObject>(arg: T): arg is SetNonNullable<T> =>
	Object.values(arg).every((a) => a !== undefined);
